/**
 * Gamma API Service
 * 
 * Integrates with Gamma.app API to generate presentations from CustomerIntelligence data.
 * Creates professional slides from management summaries.
 */

import type { CustomerIntelligence } from '../types/customerIntelligence';

// ============================================================================
// Configuration
// ============================================================================

const GAMMA_API_KEY = import.meta.env.VITE_GAMMA_API_KEY;
const GAMMA_API_URL = 'https://public-api.gamma.app/v1.0/generations';

// ============================================================================
// Types
// ============================================================================

export interface GammaPresentationOptions {
  themeId?: string;          // Optional theme ID
  numCards?: number;         // Number of slides (default: auto)
  language?: 'de' | 'en';    // Presentation language
  includeImages?: boolean;   // Whether to include images
}

export interface GammaGenerationRequest {
  format: 'presentation';
  textMode: 'preserve' | 'enhance';
  inputText: string;
  numCards?: number;
  themeId?: string;
  language?: string;
}

export interface GammaGenerationResponse {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  url?: string;
  error?: string;
  cardCount?: number;
}

export interface GammaResult {
  url: string;
  cardCount: number;
  createdAt: Date;
}

// ============================================================================
// Main Presentation Generation
// ============================================================================

/**
 * Create Gamma presentation from Customer Intelligence data
 */
export async function createPresentation(
  intelligence: CustomerIntelligence,
  options?: GammaPresentationOptions
): Promise<GammaResult> {
  try {
    console.log('📊 Creating Gamma presentation for:', intelligence.businessName);
    
    // STEP 1: Build presentation text from summaries
    const inputText = buildPresentationText(intelligence, options?.language || 'de');
    
    // STEP 2: Create generation request
    const request: GammaGenerationRequest = {
      format: 'presentation',
      textMode: 'preserve', // Keep our summaries as-is
      inputText,
      numCards: options?.numCards,
      themeId: options?.themeId,
      language: options?.language || 'de'
    };
    
    console.log('  Step 1: Sending generation request...');
    const generationId = await startGeneration(request);
    
    // STEP 3: Poll for completion
    console.log('  Step 2: Waiting for generation to complete...');
    const result = await pollGenerationStatus(generationId);
    
    if (!result.url) {
      throw new Error('Presentation generated but no URL returned');
    }
    
    console.log(`  ✅ Presentation created: ${result.url}`);
    
    return {
      url: result.url,
      cardCount: result.cardCount || 0,
      createdAt: new Date()
    };
    
  } catch (error) {
    console.error('❌ Gamma presentation creation failed:', error);
    throw new Error(`Failed to create presentation: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// ============================================================================
// Build Presentation Text
// ============================================================================

function buildPresentationText(intelligence: CustomerIntelligence, language: 'de' | 'en'): string {
  const isGerman = language === 'de';
  
  let text = '';
  
  // Slide 1: Title
  text += isGerman 
    ? `# ${intelligence.businessName}\n\nCustomer Intelligence Report\n${new Date().toLocaleDateString('de-DE')}\n\n`
    : `# ${intelligence.businessName}\n\nCustomer Intelligence Report\n${new Date().toLocaleDateString('en-US')}\n\n`;
  
  // Slide 2: Executive Summary
  text += isGerman
    ? `# Executive Summary\n\n**Branche:** ${intelligence.brand.industry}\n**Geschäftsmodell:** ${intelligence.brand.businessModel}\n**USP:** ${intelligence.brand.usp}\n\n`
    : `# Executive Summary\n\n**Industry:** ${intelligence.brand.industry}\n**Business Model:** ${intelligence.brand.businessModel}\n**USP:** ${intelligence.brand.usp}\n\n`;
  
  // Slide 3-4: Products & Services
  if (intelligence.productServices?.summary) {
    text += isGerman
      ? `# Produkte & Dienstleistungen\n\n${intelligence.productServices.summary}\n\n`
      : `# Products & Services\n\n${intelligence.productServices.summary}\n\n`;
    
    // Add details
    if (intelligence.productServices.products.length > 0) {
      text += isGerman ? '## Produkte\n\n' : '## Products\n\n';
      intelligence.productServices.products.slice(0, 5).forEach(product => {
        text += `- **${product.name}**`;
        if (product.description) text += `: ${product.description}`;
        text += '\n';
      });
      text += '\n';
    }
    
    if (intelligence.productServices.services.length > 0) {
      text += isGerman ? '## Dienstleistungen\n\n' : '## Services\n\n';
      intelligence.productServices.services.slice(0, 5).forEach(service => {
        text += `- **${service.name}**`;
        if (service.description) text += `: ${service.description}`;
        text += '\n';
      });
      text += '\n';
    }
  }
  
  // Slide 5-6: Competitor Analysis
  if (intelligence.enhancedCompetitorAnalysis?.summary) {
    text += isGerman
      ? `# Wettbewerbsanalyse\n\n${intelligence.enhancedCompetitorAnalysis.summary}\n\n`
      : `# Competitive Analysis\n\n${intelligence.enhancedCompetitorAnalysis.summary}\n\n`;
    
    // Add key insights
    if (intelligence.enhancedCompetitorAnalysis.insights) {
      const insights = intelligence.enhancedCompetitorAnalysis.insights;
      text += isGerman ? '## Marktposition\n\n' : '## Market Position\n\n';
      text += `${insights.marketPosition}\n\n`;
      
      if (insights.strengths.length > 0) {
        text += isGerman ? '**Stärken:**\n' : '**Strengths:**\n';
        insights.strengths.forEach(s => text += `- ${s}\n`);
        text += '\n';
      }
      
      if (insights.opportunities.length > 0) {
        text += isGerman ? '**Chancen:**\n' : '**Opportunities:**\n';
        insights.opportunities.forEach(o => text += `- ${o}\n`);
        text += '\n';
      }
    }
  }
  
  // Slide 7-8: Technical Review
  if (intelligence.technicalReview?.summary) {
    text += isGerman
      ? `# Technische Bewertung\n\n${intelligence.technicalReview.summary}\n\n`
      : `# Technical Assessment\n\n${intelligence.technicalReview.summary}\n\n`;
    
    // Add key technologies
    if (intelligence.technicalReview.technologies.length > 0) {
      text += isGerman ? '## Erkannte Technologien\n\n' : '## Detected Technologies\n\n';
      const topTech = intelligence.technicalReview.technologies
        .sort((a, b) => b.confidence - a.confidence)
        .slice(0, 8);
      topTech.forEach(tech => {
        text += `- **${tech.name}** (${tech.category})\n`;
      });
      text += '\n';
    }
    
    // Add performance scores
    if (intelligence.technicalReview.pageSpeedMobile) {
      const mobile = intelligence.technicalReview.pageSpeedMobile;
      text += isGerman 
        ? `**Performance (Mobil):** ${mobile.performanceScore}/100\n\n`
        : `**Performance (Mobile):** ${mobile.performanceScore}/100\n\n`;
    }
  }
  
  // Slide 9-10: Industry Research
  if (intelligence.industryResearch?.summary) {
    text += isGerman
      ? `# Branchenentwicklung\n\n${intelligence.industryResearch.summary}\n\n`
      : `# Industry Trends\n\n${intelligence.industryResearch.summary}\n\n`;
    
    // Add key trends
    if (intelligence.industryResearch.industryTrends.length > 0) {
      text += isGerman ? '## Aktuelle Trends\n\n' : '## Current Trends\n\n';
      intelligence.industryResearch.industryTrends
        .filter(t => t.relevance === 'high')
        .slice(0, 3)
        .forEach(trend => {
          text += `- **${trend.title}:** ${trend.description}\n`;
        });
      text += '\n';
    }
    
    // Add opportunities
    if (intelligence.industryResearch.opportunities.length > 0) {
      text += isGerman ? '## Marktchancen\n\n' : '## Market Opportunities\n\n';
      intelligence.industryResearch.opportunities.slice(0, 3).forEach(opp => {
        text += `- ${opp}\n`;
      });
      text += '\n';
    }
  }

  // Slide: Audit Scorecard (snapshot)
  if (intelligence.audit?.scorecard) {
    const sc = intelligence.audit.scorecard;
    text += isGerman
      ? `# Audit Scorecard\n\n**Overall:** ${sc.overall}/100\n\n`
      : `# Audit Scorecard\n\n**Overall:** ${sc.overall}/100\n\n`;

    const subs = sc.subscores || {};
    const rows: Array<[string, any]> = [
      [isGerman ? 'Performance' : 'Performance', subs.performance],
      [isGerman ? 'SEO' : 'SEO', subs.seo],
      [isGerman ? 'Tracking/Compliance' : 'Tracking/Compliance', subs.trackingCompliance],
      [isGerman ? 'CRO' : 'CRO', subs.cro],
      [isGerman ? 'Trust' : 'Trust', subs.trust]
    ];

    text += isGerman ? '## Subscores\n\n' : '## Subscores\n\n';
    rows.forEach(([label, val]) => {
      if (typeof val === 'number') {
        text += `- **${label}:** ${val}/100\n`;
      }
    });
    text += '\n';

    if (Array.isArray(sc.topDrivers) && sc.topDrivers.length > 0) {
      text += isGerman ? '## Top Treiber\n\n' : '## Key Drivers\n\n';
      sc.topDrivers.slice(0, 6).forEach((d: any) => (text += `- ${d}\n`));
      text += '\n';
    }
  }

  // Slide: Coverage (archetypes)
  if (intelligence.audit?.coverage) {
    const cov = intelligence.audit.coverage;
    text += isGerman
      ? `# Coverage (Seitentypen)\n\n`
      : `# Coverage (Page archetypes)\n\n`;

    if (Array.isArray(cov.found) && cov.found.length > 0) {
      text += isGerman ? `**Gefunden:** ${cov.found.join(', ')}\n\n` : `**Found:** ${cov.found.join(', ')}\n\n`;
    }
    if (Array.isArray(cov.missing) && cov.missing.length > 0) {
      text += isGerman ? '**Fehlend:**\n' : '**Missing:**\n';
      cov.missing.slice(0, 10).forEach((m: any) => (text += `- ${m}\n`));
      text += '\n';
    }
  }

  // Slide: SEO & CRO highlights (audit)
  if (intelligence.audit?.seo || intelligence.audit?.cro) {
    text += isGerman ? `# SEO & CRO (Highlights)\n\n` : `# SEO & CRO (Highlights)\n\n`;

    if (intelligence.audit?.seo) {
      const s = intelligence.audit.seo;
      text += isGerman ? '## SEO\n\n' : '## SEO\n\n';
      text += isGerman ? `**Seiten gescannt:** ${s.pagesScanned}\n\n` : `**Pages scanned:** ${s.pagesScanned}\n\n`;

      const issues = s.issues || ({} as any);
      ([
        'missingTitle',
        'missingDescription',
        'missingH1',
        'missingCanonical',
        'duplicateTitle',
        'duplicateDescription',
        'noindex'
      ] as const).forEach((k) => {
        if (typeof (issues as any)[k] === 'number') {
          text += `- **${k}:** ${(issues as any)[k]}\n`;
        }
      });
      text += '\n';
    }

    if (intelligence.audit?.cro) {
      const c = intelligence.audit.cro;
      text += isGerman ? '## CRO\n\n' : '## CRO\n\n';
      if (typeof c.formFrictionScore === 'number') text += `- **Form friction:** ${c.formFrictionScore}/100\n`;
      if (typeof c.trustSignalsScore === 'number') text += `- **Trust:** ${c.trustSignalsScore}/100\n`;
      if (Array.isArray(c.primaryCtas) && c.primaryCtas.length > 0) {
        text += isGerman ? '\n**Primäre CTAs:**\n' : '\n**Primary CTAs:**\n';
        c.primaryCtas.slice(0, 6).forEach((x: any) => (text += `- ${x}\n`));
      }
      if (Array.isArray(c.topFixes) && c.topFixes.length > 0) {
        text += isGerman ? '\n**Top Fixes:**\n' : '\n**Top Fixes:**\n';
        c.topFixes.slice(0, 6).forEach((x: any) => (text += `- ${x}\n`));
      }
      text += '\n';
    }
  }

  // Slide: Tracking & Compliance (audit)
  if (intelligence.audit?.trackingCompliance) {
    const tc = intelligence.audit.trackingCompliance;
    text += isGerman ? `# Tracking & Compliance\n\n` : `# Tracking & Compliance\n\n`;

    text += isGerman
      ? `**CMP:** ${(tc.cmpVendors || []).join(', ') || '—'}\n\n`
      : `**CMP:** ${(tc.cmpVendors || []).join(', ') || '—'}\n\n`;

    text += isGerman
      ? `**Consent Mode:** ${tc.consentModeDetected ? 'Detected' : 'Not detected'}\n\n`
      : `**Consent Mode:** ${tc.consentModeDetected ? 'Detected' : 'Not detected'}\n\n`;

    if (Array.isArray(tc.risks) && tc.risks.length > 0) {
      text += isGerman ? '## Risiken\n\n' : '## Risks\n\n';
      tc.risks.slice(0, 6).forEach((r: any) => (text += `- ${r}\n`));
      text += '\n';
    }
    if (Array.isArray(tc.recommendations) && tc.recommendations.length > 0) {
      text += isGerman ? '## Empfehlungen\n\n' : '## Recommendations\n\n';
      tc.recommendations.slice(0, 6).forEach((r: any) => (text += `- ${r}\n`));
      text += '\n';
    }
  }

  // Slide: Offer & Pricing (audit)
  if (intelligence.audit?.offerPricing) {
    const op = intelligence.audit.offerPricing;
    text += isGerman ? `# Offer & Pricing\n\n` : `# Offer & Pricing\n\n`;
    text += isGerman
      ? `**Pricing detected:** ${op.pricingDetected ? 'Yes' : 'No'}\n\n`
      : `**Pricing detected:** ${op.pricingDetected ? 'Yes' : 'No'}\n\n`;

    if (typeof op.offerClarityScore === 'number') {
      text += `**Offer clarity:** ${op.offerClarityScore}/100\n\n`;
    }
    if (Array.isArray(op.gaps) && op.gaps.length > 0) {
      text += isGerman ? '## Gaps\n\n' : '## Gaps\n\n';
      op.gaps.slice(0, 6).forEach((g: any) => (text += `- ${g}\n`));
      text += '\n';
    }
    if (Array.isArray(op.leadMagnets) && op.leadMagnets.length > 0) {
      text += isGerman ? '## Lead Magnets\n\n' : '## Lead Magnets\n\n';
      op.leadMagnets.slice(0, 6).forEach((m: any) => (text += `- ${m.type || 'asset'}: ${m.url}\n`));
      text += '\n';
    }
  }

  // Slide: Action Plan (7/30/90)
  if (intelligence.actionPlan?.items && intelligence.actionPlan.items.length > 0) {
    text += isGerman
      ? `# Aktionsplan (7/30/90)\n\n${intelligence.actionPlan.summary || ''}\n\n`
      : `# Action Plan (7/30/90)\n\n${intelligence.actionPlan.summary || ''}\n\n`;

    const byTf = {
      '7d': intelligence.actionPlan.items.filter(i => i.timeframe === '7d'),
      '30d': intelligence.actionPlan.items.filter(i => i.timeframe === '30d'),
      '90d': intelligence.actionPlan.items.filter(i => i.timeframe === '90d')
    };

    (['7d', '30d', '90d'] as const).forEach(tf => {
      const items = byTf[tf].slice(0, 4);
      text += isGerman
        ? `## ${tf === '7d' ? '7 Tage (Quick Wins)' : tf === '30d' ? '30 Tage' : '90 Tage'}\n\n`
        : `## ${tf === '7d' ? '7 days (quick wins)' : tf === '30d' ? '30 days' : '90 days'}\n\n`;
      if (items.length === 0) {
        text += '- —\n\n';
      } else {
        items.forEach(it => {
          text += `- **${it.title}** (${it.category}) — Impact: ${it.impact}, Effort: ${it.effort}, Confidence: ${it.confidence}%\n`;
        });
        text += '\n';
      }
    });
  }

  // Slide: Tracking Readiness
  if (intelligence.trackingReadiness) {
    const tr = intelligence.trackingReadiness;
    text += isGerman
      ? `# Tracking & Conversion Readiness\n\n**Status:** ${tr.status}\n\n`
      : `# Tracking & Conversion Readiness\n\n**Status:** ${tr.status}\n\n`;

    if (tr.missing.length > 0) {
      text += isGerman ? '**Fehlend:**\n' : '**Missing:**\n';
      tr.missing.slice(0, 6).forEach(m => (text += `- ${m}\n`));
      text += '\n';
    }

    if (tr.recommendations.length > 0) {
      text += isGerman ? '**Empfehlungen:**\n' : '**Recommendations:**\n';
      tr.recommendations.slice(0, 6).forEach(r => (text += `- ${r}\n`));
      text += '\n';
    }
  }

  // Slide: Competitor Gaps / Winning Angles
  if (intelligence.enhancedCompetitorAnalysis && (
    (intelligence.enhancedCompetitorAnalysis.gaps && intelligence.enhancedCompetitorAnalysis.gaps.length > 0)
    || (intelligence.enhancedCompetitorAnalysis.winningAngles && intelligence.enhancedCompetitorAnalysis.winningAngles.length > 0)
  )) {
    const comp = intelligence.enhancedCompetitorAnalysis;
    text += isGerman
      ? `# Wettbewerbs-Gaps & Winning Angles\n\n`
      : `# Competitive Gaps & Winning Angles\n\n`;

    if (comp.gaps && comp.gaps.length > 0) {
      text += isGerman ? '## Gaps (Chancen)\n\n' : '## Gaps (Opportunities)\n\n';
      comp.gaps.slice(0, 6).forEach(g => (text += `- ${g}\n`));
      text += '\n';
    }
    if (comp.winningAngles && comp.winningAngles.length > 0) {
      text += isGerman ? '## Winning Angles\n\n' : '## Winning Angles\n\n';
      comp.winningAngles.slice(0, 6).forEach(a => (text += `- ${a}\n`));
      text += '\n';
    }
    if (comp.messagingSuggestions && comp.messagingSuggestions.length > 0) {
      text += isGerman ? '## Messaging\n\n' : '## Messaging\n\n';
      comp.messagingSuggestions.slice(0, 4).forEach(m => (text += `- ${m}\n`));
      text += '\n';
    }
  }
  
  // Slide 11: Recommendations
  text += isGerman
    ? `# Handlungsempfehlungen\n\n`
    : `# Recommendations\n\n`;
  
  const recommendations: string[] = [];

  // Prefer the AI action plan (top items) if available
  if (intelligence.actionPlan?.items && intelligence.actionPlan.items.length > 0) {
    intelligence.actionPlan.items.slice(0, 6).forEach(it => recommendations.push(it.title));
  }
  
  if (intelligence.enhancedCompetitorAnalysis?.insights?.opportunities) {
    recommendations.push(...intelligence.enhancedCompetitorAnalysis.insights.opportunities.slice(0, 2));
  }
  
  if (intelligence.industryResearch?.opportunities) {
    recommendations.push(...intelligence.industryResearch.opportunities.slice(0, 2));
  }
  
  if (recommendations.length > 0) {
    recommendations.forEach((rec, idx) => {
      text += `${idx + 1}. ${rec}\n`;
    });
  } else {
    text += isGerman
      ? '- Analyse abschließen um Empfehlungen zu generieren\n'
      : '- Complete analysis to generate recommendations\n';
  }
  text += '\n';

  // Slide: Sources / citations
  const sourceMap = new Map<string, string | undefined>();
  const pushEvidence = (arr?: any[]) => {
    if (!Array.isArray(arr)) return;
    arr.forEach(e => {
      const url = typeof e?.url === 'string' ? e.url : '';
      if (!url || sourceMap.has(url)) return;
      sourceMap.set(url, typeof e?.title === 'string' ? e.title : undefined);
    });
  };
  pushEvidence(intelligence.evidence?.productServices as any);
  pushEvidence(intelligence.evidence?.technicalReview as any);
  pushEvidence(intelligence.evidence?.industryResearch as any);
  pushEvidence(intelligence.evidence?.competitorAnalysis as any);
  pushEvidence(intelligence.evidence?.actionPlan as any);
  pushEvidence(intelligence.evidence?.trackingReadiness as any);
  pushEvidence(intelligence.audit?.coverage?.evidence as any);
  pushEvidence(intelligence.audit?.seo?.evidence as any);
  pushEvidence(intelligence.audit?.cro?.evidence as any);
  pushEvidence(intelligence.audit?.trackingCompliance?.evidence as any);
  pushEvidence(intelligence.audit?.offerPricing?.evidence as any);
  (intelligence.actionPlan?.items || []).forEach(it => pushEvidence((it as any).evidence));

  const sources = Array.from(sourceMap.entries()).slice(0, 10);
  if (sources.length > 0) {
    text += isGerman ? `# Quellen (Auszug)\n\n` : `# Sources (excerpt)\n\n`;
    sources.forEach(([url, title]) => {
      text += `- ${title ? `**${title}**: ` : ''}${url}\n`;
    });
    text += '\n';
  }
  
  // Slide 12: Next Steps
  text += isGerman
    ? `# Nächste Schritte\n\n**Kontakt:**\n${intelligence.location?.city || ''}\n\n**Status:** ${intelligence.lifecycle.status}\n\nBereit für die Zusammenarbeit?\n`
    : `# Next Steps\n\n**Contact:**\n${intelligence.location?.city || ''}\n\n**Status:** ${intelligence.lifecycle.status}\n\nReady to work together?\n`;
  
  return text;
}

// ============================================================================
// API Communication
// ============================================================================

/**
 * Start generation via Gamma API
 */
async function startGeneration(request: GammaGenerationRequest): Promise<string> {
  try {
    if (!GAMMA_API_KEY) {
      throw new Error('Gamma API key is not configured. Use the server-side createGammaPresentation function for customer analysis presentations.');
    }

    const response = await fetch(GAMMA_API_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${GAMMA_API_KEY}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(request)
    });
    
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Gamma API error: ${response.status} - ${error}`);
    }
    
    const data = await response.json();
    
    if (!data.id) {
      throw new Error('No generation ID returned from Gamma API');
    }
    
    return data.id;
    
  } catch (error) {
    console.error('❌ Failed to start Gamma generation:', error);
    throw error;
  }
}

/**
 * Poll generation status until complete
 */
async function pollGenerationStatus(
  generationId: string,
  maxAttempts: number = 60,
  intervalMs: number = 2000
): Promise<GammaGenerationResponse> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const response = await fetch(`${GAMMA_API_URL}/${generationId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${GAMMA_API_KEY}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Gamma API polling error: ${response.status}`);
      }
      
      const data: GammaGenerationResponse = await response.json();
      
      console.log(`    Poll ${attempt + 1}/${maxAttempts}: ${data.status}`);
      
      if (data.status === 'completed') {
        return data;
      }
      
      if (data.status === 'failed') {
        throw new Error(`Generation failed: ${data.error || 'Unknown error'}`);
      }
      
      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, intervalMs));
      
    } catch (error) {
      if (attempt === maxAttempts - 1) {
        throw error;
      }
      // Continue polling
    }
  }
  
  throw new Error('Generation timeout - max polling attempts reached');
}

// ============================================================================
// Export
// ============================================================================

export const gammaService = {
  createPresentation,
  buildPresentationText
};
